import { parseISO } from 'date-fns';
import { formatYYYYMMDD } from '../../../../utils/utils';
import moment from 'moment';
import { ExpiryTerm } from '../interfaces/ExpiryTerm';

function getDateOfExpiry({
  expiryTermYear,
  expiryTermMonth,
  expiryTermDay,
  dateOfPurchase,
}) {
  expiryTermYear = parseInt(expiryTermYear);
  expiryTermMonth = parseInt(expiryTermMonth);
  expiryTermDay = parseInt(expiryTermDay);
  if (isNaN(expiryTermYear) && isNaN(expiryTermMonth) && isNaN(expiryTermDay))
    return null;

  const dateOfPurchaseMoment = moment(dateOfPurchase);
  if (!dateOfPurchaseMoment.isValid()) return null;

  expiryTermYear = isNaN(expiryTermYear) ? 0 : expiryTermYear;
  expiryTermMonth = isNaN(expiryTermMonth) ? 0 : expiryTermMonth;
  expiryTermDay = isNaN(expiryTermDay) ? 0 : expiryTermDay;
  return parseISO(
    dateOfPurchaseMoment
      .add(expiryTermYear, 'years')
      .add(expiryTermMonth, 'months')
      .add(expiryTermDay, 'days')
      .format(formatYYYYMMDD),
  );
}

function getExpiryTerm(dateOfPurchase, dateOfExpiry): ExpiryTerm {
  const expiryTerm: ExpiryTerm = {
    equipment_expiry_term_year: null,
    equipment_expiry_term_month: null,
    equipment_expiry_term_day: null,
    equipment_expiry_term: '',
  };
  const dateOfPurchaseMoment = moment(dateOfPurchase);
  if (!dateOfPurchaseMoment.isValid()) return expiryTerm;

  const dateOfExpiryMoment = moment(dateOfExpiry);
  if (!dateOfExpiryMoment.isValid()) return expiryTerm;

  if (dateOfExpiryMoment.isBefore(dateOfPurchaseMoment)) return expiryTerm;

  const expiryTermYear = dateOfExpiryMoment.diff(dateOfPurchaseMoment, 'year');
  dateOfPurchaseMoment.add(expiryTermYear, 'years');

  const expiryTermMonth = dateOfExpiryMoment.diff(
    dateOfPurchaseMoment,
    'month',
  );
  dateOfPurchaseMoment.add(expiryTermMonth, 'months');

  const expiryTermDay = dateOfExpiryMoment.diff(dateOfPurchaseMoment, 'day');

  expiryTerm.equipment_expiry_term_year = expiryTermYear;
  expiryTerm.equipment_expiry_term_month = expiryTermMonth;
  expiryTerm.equipment_expiry_term_day = expiryTermDay;
  expiryTerm.equipment_expiry_term = formatExpiryTerm({
    expiryTermYear,
    expiryTermMonth,
    expiryTermDay,
  });
  return expiryTerm;
}

function formatExpiryTerm({ expiryTermYear, expiryTermMonth, expiryTermDay }) {
  expiryTermYear = parseInt(expiryTermYear);
  expiryTermMonth = parseInt(expiryTermMonth);
  expiryTermDay = parseInt(expiryTermDay);
  if (isNaN(expiryTermYear) && isNaN(expiryTermMonth) && isNaN(expiryTermDay))
    return '';

  expiryTermYear = isNaN(expiryTermYear) ? 0 : expiryTermYear;
  expiryTermMonth = isNaN(expiryTermMonth) ? 0 : expiryTermMonth;
  expiryTermDay = isNaN(expiryTermDay) ? 0 : expiryTermDay;

  return [
    expiryTermYear,
    'year',
    expiryTermMonth,
    'month',
    expiryTermDay,
    'day',
  ].join(' ');
}

function getWarrantyInformation({
  dateOfPurchase,
  dateOfExpiry,
  expiryTerm,
  expiryTermYear,
  expiryTermMonth,
  expiryTermDay,
}) {
  const warrantyInformation = {
    equipment_date_of_purchase: dateOfPurchase,
    equipment_date_of_expiry: dateOfExpiry,
    equipment_expiry_term: expiryTerm,
    equipment_expiry_term_year: expiryTermYear,
    equipment_expiry_term_month: expiryTermMonth,
    equipment_expiry_term_day: expiryTermDay,
  };

  if (!dateOfPurchase) return warrantyInformation;

  if (dateOfExpiry) {
    return {
      ...warrantyInformation,
      ...getExpiryTerm(dateOfPurchase, dateOfExpiry),
    };
  }

  return {
    ...warrantyInformation,
    ['equipment_date_of_expiry']:
      getDateOfExpiry({
        expiryTermYear,
        expiryTermMonth,
        expiryTermDay,
        dateOfPurchase,
      }) ?? dateOfExpiry,
    ['equipment_expiry_term']: formatExpiryTerm({
      expiryTermYear,
      expiryTermMonth,
      expiryTermDay,
    }),
  };
}

function isPurchaseDateBeforeExpiryDate(dateOfPurchase, dateOfExpiry) {
  const dateOfPurchaseMoment = moment(dateOfPurchase);
  const dateOfExpiryMoment = moment(dateOfExpiry);
  if (
    dateOfPurchaseMoment.isValid() &&
    dateOfExpiryMoment.isValid() &&
    dateOfPurchaseMoment.isAfter(dateOfExpiryMoment)
  ) {
    return false;
  }
  return true;
}

function formatDate(date) {
  if (!date) return null;
  const dateObject = moment(date);
  if (!dateObject.isValid()) return null;
  return dateObject.startOf('day').toDate();
}

export {
  getExpiryTerm,
  getDateOfExpiry,
  formatExpiryTerm,
  getWarrantyInformation,
  isPurchaseDateBeforeExpiryDate,
  formatDate,
};
